import { useEffect, useMemo, useState } from "react";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, CURRENCIES, fmtErr, formatCurrency, inr, today, GST_RATES, STATES, TDS_RATES, isUpgradeError, toastUpgrade } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const emptyRow = () => ({
  key: crypto.randomUUID?.() ?? `k${Date.now()}${Math.random()}`,
  itemId: "", name: "", hsn: "", qty: 1, rate: 0, discount: 0, gst: 18,
  boxes: "", mrp: "", retail_price: "", batch_no: "", mfg_date: "", exp_date: "",
});

const NOTE_REASONS = ["Sales Return", "Purchase Return", "Post Sale Discount", "Rate Difference", "Defective / Deficiency", "Correction in Invoice", "Others"];

const emptyParty = {
  name: "", phone: "", email: "", gstin: "", state: "Maharashtra",
  address: "", district: "", pin_code: "", currency: "INR",
};
const emptyShipTo = { address: "", district: "", state: "", pin_code: "" };
const emptyItem = { name: "", hsn: "", gst_rate: 18, sale_price: "", purchase_price: "", batch_no: "", mfg_date: "", exp_date: "" };

export function InvoiceForm({ type, onSaved, open: controlledOpen, onOpenChange }) {
  const isSale = type === "sale";
  const isPurchase = type === "purchase";
  const isNote = type === "credit_note" || type === "debit_note";
  const isMoneyDoc = isSale || isPurchase;
  const partyKind = isSale || type === "estimate" || type === "credit_note" ? "customer" : "vendor";
  const ENDPOINTS = { sale: "/sales", purchase: "/purchases", estimate: "/estimates", po: "/purchase-orders", credit_note: "/credit-notes", debit_note: "/debit-notes" };
  const TITLES = { sale: "Sales Invoice", purchase: "Purchase Bill", estimate: "Estimate", po: "Purchase Order", credit_note: "Credit Note", debit_note: "Debit Note" };
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [partyId, setPartyId] = useState("");
  const [exchangeRate, setExchangeRate] = useState("1");
  const [shipToDifferent, setShipToDifferent] = useState(false);
  const [shipTo, setShipTo] = useState(emptyShipTo);
  const [date, setDate] = useState(today());
  const [pos, setPos] = useState("");
  const [rows, setRows] = useState([emptyRow()]);
  const [paidNow, setPaidNow] = useState("");
  const [accountId, setAccountId] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [refDocs, setRefDocs] = useState([]);
  const [refDocId, setRefDocId] = useState("");
  const [reason, setReason] = useState("");
  const [tdsChoice, setTdsChoice] = useState("0");
  const [tdsCustom, setTdsCustom] = useState("");
  const [quickParty, setQuickParty] = useState({ open: false, saving: false, form: emptyParty });
  const [quickItem, setQuickItem] = useState({ open: false, saving: false, row: 0, form: emptyItem });
  const filteredRefDocs = useMemo(() => refDocs.filter((d) => d.party_id === partyId), [refDocs, partyId]);

  const pharma = settings?.industry === "pharma";
  const showBoxes = !!settings?.col_boxes;
  const showMrp = !!settings?.col_mrp;
  const showRetail = !!settings?.col_retail_price;
  const tdsRate = tdsChoice === "custom" ? Number(tdsCustom) || 0 : Number(tdsChoice) || 0;

  useEffect(() => {
    if (!open) return;
    const reqs = [
      API.get(`/parties?type=${partyKind}`),
      API.get("/items"),
      API.get("/banking/accounts"),
      API.get("/settings"),
    ];
    if (isNote) reqs.push(API.get(type === "credit_note" ? "/sales" : "/purchases"));
    Promise.all(reqs)
      .then(([p, i, a, s, r]) => {
        setParties(p.data);
        setItems(i.data);
        setAccounts(a.data);
        setSettings(s.data);
        setRefDocs(r ? r.data : []);
      })
      .catch((e) => toast.error(fmtErr(e)));
  }, [open, partyKind, isNote, type]);

  const onParty = (id) => {
    setPartyId(id);
    const p = parties.find((x) => x.id === id);
    setPos(p?.state || settings?.state || "");
    setExchangeRate("1");
  };

  const partyLabel = partyKind === "customer" ? "customer" : "vendor";
  const selectedParty = parties.find((party) => party.id === partyId);
  const currency = (isSale || type === "estimate") ? (selectedParty?.currency || "INR") : "INR";
  const invoiceExchangeRate = currency === "INR" ? 1 : Number(exchangeRate);
  const exchangeDivisor = currency === "INR" || !Number.isFinite(invoiceExchangeRate) || invoiceExchangeRate <= 0 ? 1 : invoiceExchangeRate;
  const supportsShippingAddress = isSale || type === "estimate";

  const formatPartyAddress = (party) => [
    party?.address,
    [party?.district, party?.state, party?.pin_code].filter(Boolean).join(", "),
    party?.phone,
    party?.email,
    party?.gstin && `GSTIN: ${party.gstin}`,
  ].filter(Boolean);

  const intra =
    (pos || settings?.state || "").trim().toLowerCase() === (settings?.state || "").trim().toLowerCase();

  const totals = useMemo(() => {
    let taxable = 0,
      cgst = 0,
      sgst = 0,
      igst = 0;
    rows.forEach((r) => {
      const base = (Number(r.qty) || 0) * (Number(r.rate) || 0);
      const tx = base - (base * (Number(r.discount) || 0)) / 100;
      const tax = (tx * (Number(r.gst) || 0)) / 100;
      taxable += tx;
      if (intra) {
        cgst += tax / 2;
        sgst += tax / 2;
      } else {
        igst += tax;
      }
    });
    const total = taxable + cgst + sgst + igst;
    const tds = isMoneyDoc ? (taxable * tdsRate) / 100 : 0;
    return { taxable, cgst, sgst, igst, total, tds, net: total - tds };
  }, [rows, intra, tdsRate, isMoneyDoc]);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const pickItem = (i, itemId) => {
    if (itemId === "__new__") {
      setQuickItem({ open: true, saving: false, row: i, form: emptyItem });
      return;
    }
    if (itemId === "custom") {
      setRow(i, { itemId: "" });
      return;
    }
    const it = items.find((x) => x.id === itemId);
    if (it)
      setRow(i, {
        itemId,
        name: it.name,
        hsn: it.hsn || "",
        rate: (isPurchase || type === "po" || type === "debit_note" ? it.purchase_price : it.sale_price) / exchangeDivisor,
        mrp: Number(it.mrp || 0) / exchangeDivisor,
        retail_price: Number(it.retail_price || 0) / exchangeDivisor,
        gst: it.gst_rate,
        batch_no: it.batch_no || "",
        mfg_date: it.mfg_date || "",
        exp_date: it.exp_date || "",
      });
  };

  const saveQuickParty = async () => {
    const f = quickParty.form;
    if (!f.name.trim()) return toast.error("Party name required");
    if (f.pin_code && !/^\d{6}$/.test(f.pin_code)) return toast.error("PIN code must be 6 digits");
    setQuickParty((q) => ({ ...q, saving: true }));
    try {
      const r = await API.post("/parties", {
        ...f,
        name: f.name.trim(),
        type: partyKind,
      });
      setParties((ps) => [...ps, r.data].sort((a, b) => a.name.localeCompare(b.name)));
      setPartyId(r.data.id);
      setPos(r.data.state || settings?.state || "");
      toast.success(`${partyLabel === "customer" ? "Customer" : "Vendor"} added`);
      setQuickParty({ open: false, saving: false, form: emptyParty });
    } catch (e) {
      toast.error(fmtErr(e));
      setQuickParty((q) => ({ ...q, saving: false }));
    }
  };

  const saveQuickItem = async () => {
    const f = quickItem.form;
    if (!f.name.trim()) return toast.error("Item name required");
    setQuickItem((q) => ({ ...q, saving: true }));
    try {
      const r = await API.post("/items", {
        name: f.name.trim(), hsn: f.hsn, gst_rate: Number(f.gst_rate) || 0,
        sale_price: (Number(f.sale_price) || 0) * exchangeDivisor, purchase_price: (Number(f.purchase_price) || 0) * exchangeDivisor,
        batch_no: f.batch_no || "", mfg_date: f.mfg_date || "", exp_date: f.exp_date || "",
      });
      setItems((is) => [...is, r.data].sort((a, b) => a.name.localeCompare(b.name)));
      const rowIdx = quickItem.row;
      setQuickItem({ open: false, saving: false, row: 0, form: emptyItem });
      const it = r.data;
      setRow(rowIdx, {
        itemId: it.id, name: it.name, hsn: it.hsn || "",
        rate: (isPurchase || type === "po" || type === "debit_note" ? it.purchase_price : it.sale_price) / exchangeDivisor,
        mrp: Number(it.mrp || 0) / exchangeDivisor,
        retail_price: Number(it.retail_price || 0) / exchangeDivisor,
        gst: it.gst_rate,
        batch_no: it.batch_no || "", mfg_date: it.mfg_date || "", exp_date: it.exp_date || "",
      });
      toast.success("Item added");
    } catch (e) {
      toast.error(fmtErr(e));
      setQuickItem((q) => ({ ...q, saving: false }));
    }
  };

  const lineAmount = (r) => {
    const base = (Number(r.qty) || 0) * (Number(r.rate) || 0);
    const tx = base - (base * (Number(r.discount) || 0)) / 100;
    return tx + (tx * (Number(r.gst) || 0)) / 100;
  };

  const submit = async () => {
    if (!partyId) return toast.error(`Select a ${partyLabel}`);
    if (currency !== "INR" && (!Number.isFinite(invoiceExchangeRate) || invoiceExchangeRate <= 0)) return toast.error("Enter a valid INR exchange rate");
    if (shipToDifferent && !shipTo.address.trim()) return toast.error("Enter the shipping address");
    if (shipToDifferent && shipTo.pin_code && !/^\d{6}$/.test(shipTo.pin_code)) return toast.error("Shipping PIN code must be 6 digits");
    if (isNote && !reason) return toast.error("Select a reason");
    if (rows.some((r) => !r.name.trim() || Number(r.qty) <= 0)) return toast.error("Fill item name and quantity in every row");
    setSaving(true);
    try {
      await API.post(ENDPOINTS[type], {
        date,
        party_id: partyId,
        ship_to_different: supportsShippingAddress && shipToDifferent,
        shipping_address: supportsShippingAddress && shipToDifferent ? shipTo.address.trim() : "",
        shipping_district: supportsShippingAddress && shipToDifferent ? shipTo.district.trim() : "",
        shipping_state: supportsShippingAddress && shipToDifferent ? shipTo.state.trim() : "",
        shipping_pin_code: supportsShippingAddress && shipToDifferent ? shipTo.pin_code : "",
        place_of_supply: pos || undefined,
        items: rows.map((r) => ({
          item_id: r.itemId || null,
          name: r.name,
          hsn: r.hsn,
          qty: Number(r.qty),
          rate: Number(r.rate),
          discount_pct: Number(r.discount) || 0,
          gst_rate: Number(r.gst) || 0,
          unit: "pcs",
          boxes: Number(r.boxes) || 0,
          mrp: Number(r.mrp) || 0,
          retail_price: Number(r.retail_price) || 0,
          batch_no: r.batch_no || "",
          mfg_date: r.mfg_date || "",
          exp_date: r.exp_date || "",
        })),
        notes,
        currency,
        exchange_rate: invoiceExchangeRate,
        paid_amount: isMoneyDoc ? Number(paidNow) || 0 : 0,
        account_id: isMoneyDoc ? accountId || null : null,
        ref_doc_id: isNote ? refDocId || null : null,
        reason: isNote ? reason : "",
        tds_rate: isMoneyDoc ? tdsRate : 0,
      });
      toast.success(`${TITLES[type]} created`);
      setOpen(false);
      setRows([emptyRow()]);
      setPaidNow("");
      setNotes("");
      setPartyId("");
      setExchangeRate("1");
      setShipToDifferent(false);
      setShipTo(emptyShipTo);
      setAccountId("");
      setRefDocId("");
      setReason("");
      setTdsChoice("0");
      setTdsCustom("");
      onSaved?.();
    } catch (e) {
      if (isUpgradeError(e)) {
        toastUpgrade(e);
      } else {
        toast.error(fmtErr(e));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`new-${type}-button`}>
          <Plus size={16} className="mr-1" weight="bold" /> New {TITLES[type]}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto" data-testid="invoice-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">New {TITLES[type]}</DialogTitle>
          <DialogDescription className="sr-only">Create a new GST {TITLES[type].toLowerCase()} with line items</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label>{partyKind === "customer" ? "Customer" : "Vendor"}</Label>
            <div className="flex gap-1.5">
              <Select value={partyId} onValueChange={onParty}>
                <SelectTrigger data-testid="invoice-party-select" className="flex-1">
                  <SelectValue placeholder={`Select ${partyLabel}`} />
                </SelectTrigger>
                <SelectContent>
                  {parties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button" variant="outline" size="icon" title={`Add new ${partyLabel}`}
                onClick={() => setQuickParty({ open: true, saving: false, form: emptyParty })}
                data-testid="invoice-add-party-button"
              >
                <Plus size={16} weight="bold" />
              </Button>
            </div>
            {parties.length === 0 && (
              <p className="text-xs text-muted-foreground">No {partyLabel}s yet — click + to add one.</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="invoice-date-input" />
          </div>
          {currency !== "INR" && (
            <div className="space-y-2">
              <Label>Exchange rate (1 {currency} in INR)</Label>
              <Input type="number" min="0" step="any" value={exchangeRate} onChange={(e) => setExchangeRate(e.target.value)} data-testid="invoice-exchange-rate-input" />
            </div>
          )}
          <div className="space-y-2">
            <Label>Place of Supply</Label>
            <Select value={pos} onValueChange={setPos}>
              <SelectTrigger data-testid="invoice-pos-select">
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                {STATES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground" data-testid="invoice-tax-mode">
              {settings ? (intra ? "Intra-state: CGST + SGST" : "Inter-state: IGST") : ""}
            </p>
          </div>
        </div>

        {supportsShippingAddress && selectedParty && (
          <div className={`grid gap-4 ${shipToDifferent ? "sm:grid-cols-2" : "sm:grid-cols-1"}`} data-testid="invoice-address-sections">
            <section className="space-y-2 rounded-md border p-3" data-testid="invoice-bill-to">
              <h3 className="text-sm font-semibold">Bill To</h3>
              <p className="text-sm font-medium">{selectedParty.name}</p>
              {formatPartyAddress(selectedParty).length > 0 ? (
                <div className="space-y-0.5 text-sm text-muted-foreground">
                  {formatPartyAddress(selectedParty).map((line, index) => <p key={index}>{line}</p>)}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No billing address saved for this party.</p>
              )}
            </section>
            {shipToDifferent && (
              <section className="space-y-3 rounded-md border p-3" data-testid="invoice-ship-to">
                <h3 className="text-sm font-semibold">Ship To</h3>
                <div className="space-y-2">
                  <Label htmlFor={`shipping-address-${type}`}>Address line *</Label>
                  <Input
                    id={`shipping-address-${type}`}
                    value={shipTo.address}
                    onChange={(e) => setShipTo((current) => ({ ...current, address: e.target.value }))}
                    data-testid="invoice-shipping-address-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor={`shipping-district-${type}`}>District</Label>
                    <Input
                      id={`shipping-district-${type}`}
                      value={shipTo.district}
                      onChange={(e) => setShipTo((current) => ({ ...current, district: e.target.value }))}
                      data-testid="invoice-shipping-district-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`shipping-pin-${type}`}>PIN code</Label>
                    <Input
                      id={`shipping-pin-${type}`}
                      inputMode="numeric"
                      maxLength={6}
                      value={shipTo.pin_code}
                      onChange={(e) => setShipTo((current) => ({ ...current, pin_code: e.target.value }))}
                      placeholder="6-digit PIN code"
                      data-testid="invoice-shipping-pin-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`shipping-state-${type}`}>State</Label>
                  <Select value={shipTo.state || "__none__"} onValueChange={(value) => setShipTo((current) => ({ ...current, state: value === "__none__" ? "" : value }))}>
                    <SelectTrigger id={`shipping-state-${type}`} data-testid="invoice-shipping-state-select">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Select state</SelectItem>
                      {STATES.map((state) => <SelectItem key={state} value={state}>{state}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {partyKind === "customer" && (
                  <div className="space-y-2">
                    <Label>Default invoice currency</Label>
                    <Select value={quickParty.form.currency || "INR"} onValueChange={(value) => setQuickParty((q) => ({ ...q, form: { ...q.form, currency: value } }))}>
                      <SelectTrigger data-testid="quick-party-currency-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{CURRENCIES.map((option) => <SelectItem key={option.code} value={option.code}>{option.name} ({option.code})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </section>
            )}
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <Checkbox
                checked={shipToDifferent}
                onCheckedChange={(checked) => {
                  setShipToDifferent(checked === true);
                  if (checked !== true) setShipTo(emptyShipTo);
                }}
                data-testid="invoice-different-shipping-checkbox"
              />
              Ship to a different address
            </label>
          </div>
        )}

        {isNote && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="note-reason-select">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {NOTE_REASONS.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Against {type === "credit_note" ? "invoice" : "bill"} (optional)</Label>
              <Select value={refDocId} onValueChange={setRefDocId}>
                <SelectTrigger data-testid="note-ref-select">
                  <SelectValue placeholder="Select document" />
                </SelectTrigger>
                <SelectContent>
                  {filteredRefDocs.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.number} · {d.date} · {formatCurrency(d.total / (Number(d.exchange_rate) || 1), d.currency || "INR")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Items</Label>
            <Button type="button" variant="outline" size="sm" onClick={() => setRows((rs) => [...rs, emptyRow()])} data-testid="invoice-add-row-button">
              <Plus size={14} className="mr-1" /> Add Row
            </Button>
          </div>
          {rows.map((r, i) => (
            <div key={r.key} className="space-y-2 rounded-md border p-3" data-testid={`invoice-row-${i}`}>
              <div className="grid gap-2 sm:grid-cols-12">
                <div className="sm:col-span-4">
                  <Select value={r.itemId || "custom"} onValueChange={(v) => pickItem(i, v)}>
                    <SelectTrigger data-testid={`invoice-item-select-${i}`}>
                      <SelectValue placeholder="Pick item" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__new__" data-testid={`invoice-item-new-option-${i}`}>+ New Item…</SelectItem>
                      <SelectItem value="custom">Custom item</SelectItem>
                      {items.map((it) => (
                        <SelectItem key={it.id} value={it.id}>
                          {it.name} (stock {it.stock})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input className="sm:col-span-5" placeholder="Item / service name" value={r.name} onChange={(e) => setRow(i, { name: e.target.value })} data-testid={`invoice-item-name-${i}`} />
                <Input className="sm:col-span-2" placeholder="HSN" value={r.hsn} onChange={(e) => setRow(i, { hsn: e.target.value })} data-testid={`invoice-item-hsn-${i}`} />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="sm:col-span-1 text-muted-foreground hover:text-destructive"
                  onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}
                  disabled={rows.length === 1}
                  data-testid={`invoice-row-delete-${i}`}
                >
                  <Trash size={16} />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                <div>
                  <Label className="text-xs">Qty</Label>
                  <Input type="number" min="0" step="any" value={r.qty} onChange={(e) => setRow(i, { qty: e.target.value })} data-testid={`invoice-item-qty-${i}`} />
                </div>
                <div>
                  <Label className="text-xs">Rate ({currency})</Label>
                  <Input type="number" min="0" step="any" value={r.rate} onChange={(e) => setRow(i, { rate: e.target.value })} data-testid={`invoice-item-rate-${i}`} />
                </div>
                <div>
                  <Label className="text-xs">Disc %</Label>
                  <Input type="number" min="0" max="100" step="any" value={r.discount} onChange={(e) => setRow(i, { discount: e.target.value })} data-testid={`invoice-item-discount-${i}`} />
                </div>
                <div>
                  <Label className="text-xs">GST %</Label>
                  <Select value={String(r.gst)} onValueChange={(v) => setRow(i, { gst: Number(v) })}>
                    <SelectTrigger data-testid={`invoice-item-gst-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GST_RATES.map((g) => (
                        <SelectItem key={g} value={String(g)}>
                          {g}%
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end justify-end pb-1">
                  <span className="text-sm font-semibold tabular-nums" data-testid={`invoice-item-amount-${i}`}>{formatCurrency(lineAmount(r), currency)}</span>
                </div>
              </div>
              {(showBoxes || showMrp || showRetail || pharma) && (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-6" data-testid={`invoice-row-extra-${i}`}>
                  {showBoxes && (
                    <div>
                      <Label className="text-xs">Boxes</Label>
                      <Input type="number" min="0" step="any" value={r.boxes} onChange={(e) => setRow(i, { boxes: e.target.value })} data-testid={`invoice-item-boxes-${i}`} />
                    </div>
                  )}
                  {showMrp && (
                    <div>
                      <Label className="text-xs">MRP ({currency})</Label>
                      <Input type="number" min="0" step="any" value={r.mrp} onChange={(e) => setRow(i, { mrp: e.target.value })} data-testid={`invoice-item-mrp-${i}`} />
                    </div>
                  )}
                  {showRetail && (
                    <div>
                      <Label className="text-xs">Retail Price ({currency})</Label>
                      <Input type="number" min="0" step="any" value={r.retail_price} onChange={(e) => setRow(i, { retail_price: e.target.value })} data-testid={`invoice-item-retail-${i}`} />
                    </div>
                  )}
                  {pharma && (
                    <>
                      <div>
                        <Label className="text-xs">Batch No.</Label>
                        <Input value={r.batch_no} onChange={(e) => setRow(i, { batch_no: e.target.value })} placeholder="B123" data-testid={`invoice-item-batch-${i}`} />
                      </div>
                      <div>
                        <Label className="text-xs">Mfg Date</Label>
                        <Input type="date" value={r.mfg_date} onChange={(e) => setRow(i, { mfg_date: e.target.value })} data-testid={`invoice-item-mfg-${i}`} />
                      </div>
                      <div>
                        <Label className="text-xs">Expiry Date</Label>
                        <Input type="date" value={r.exp_date} onChange={(e) => setRow(i, { exp_date: e.target.value })} data-testid={`invoice-item-exp-${i}`} />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" data-testid="invoice-notes-input" />
            </div>
            {isMoneyDoc && (
              <>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label>{isSale ? `Received now (${currency})` : `Paid now (${currency})`}</Label>
                  <Input type="number" min="0" step="any" value={paidNow} onChange={(e) => setPaidNow(e.target.value)} data-testid="invoice-paid-input" />
                </div>
                <div className="space-y-2">
                  <Label>Bank account</Label>
                  <Select value={accountId} onValueChange={setAccountId}>
                    <SelectTrigger data-testid="invoice-account-select">
                      <SelectValue placeholder="Optional" />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label>TDS % (deducted {isSale ? "by customer" : "from vendor"})</Label>
                  <Select value={tdsChoice} onValueChange={setTdsChoice}>
                    <SelectTrigger data-testid="invoice-tds-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TDS_RATES.map((t) => (
                        <SelectItem key={t} value={String(t)}>{t}%</SelectItem>
                      ))}
                      <SelectItem value="custom">Custom…</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {tdsChoice === "custom" && (
                  <div className="space-y-2">
                    <Label>Custom TDS %</Label>
                    <Input type="number" min="0" max="30" step="any" value={tdsCustom} onChange={(e) => setTdsCustom(e.target.value)} data-testid="invoice-tds-custom-input" />
                  </div>
                )}
              </div>
              </>
            )}
          </div>
          <div className="space-y-1.5 rounded-md border bg-muted/40 p-4 text-sm" data-testid="invoice-totals">
            <div className="flex justify-between"><span className="text-muted-foreground">Taxable</span><span className="tabular-nums" data-testid="invoice-taxable-total">{formatCurrency(totals.taxable, currency)}</span></div>
            {intra ? (
              <>
                <div className="flex justify-between"><span className="text-muted-foreground">CGST</span><span className="tabular-nums" data-testid="invoice-cgst-total">{formatCurrency(totals.cgst, currency)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">SGST</span><span className="tabular-nums" data-testid="invoice-sgst-total">{formatCurrency(totals.sgst, currency)}</span></div>
              </>
            ) : (
              <div className="flex justify-between"><span className="text-muted-foreground">IGST</span><span className="tabular-nums" data-testid="invoice-igst-total">{formatCurrency(totals.igst, currency)}</span></div>
            )}
            <div className="flex justify-between border-t pt-2 font-heading text-base font-bold">
              <span>Total</span><span className="tabular-nums" data-testid="invoice-grand-total">{formatCurrency(totals.total, currency)}</span>
            </div>
            {isMoneyDoc && totals.tds > 0 && (
              <>
                <div className="flex justify-between"><span className="text-muted-foreground">Less: TDS @ {tdsRate}%</span><span className="tabular-nums text-[#E02424]" data-testid="invoice-tds-amount">− {formatCurrency(totals.tds, currency)}</span></div>
                <div className="flex justify-between font-semibold"><span>Net {isSale ? "Receivable" : "Payable"}</span><span className="tabular-nums" data-testid="invoice-net-payable">{formatCurrency(totals.net, currency)}</span></div>
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="invoice-cancel-button">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="invoice-save-button">
            {saving ? "Saving…" : `Save ${TITLES[type]}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    <Dialog open={quickParty.open} onOpenChange={(v) => setQuickParty((q) => ({ ...q, open: v }))}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="quick-party-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">New {partyLabel === "customer" ? "Customer" : "Vendor"}</DialogTitle>
          <DialogDescription className="sr-only">Quickly add a {partyLabel} without leaving this form</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input value={quickParty.form.name} onChange={(e) => setQuickParty((q) => ({ ...q, form: { ...q.form, name: e.target.value } }))} data-testid="quick-party-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={quickParty.form.phone} onChange={(e) => setQuickParty((q) => ({ ...q, form: { ...q.form, phone: e.target.value } }))} data-testid="quick-party-phone-input" />
            </div>
            <div className="space-y-2">
              <Label>GSTIN</Label>
              <Input value={quickParty.form.gstin} onChange={(e) => setQuickParty((q) => ({ ...q, form: { ...q.form, gstin: e.target.value } }))} data-testid="quick-party-gstin-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label>Email (invoice recipient)</Label>
              <Input type="email" value={quickParty.form.email} onChange={(e) => setQuickParty((q) => ({ ...q, form: { ...q.form, email: e.target.value } }))} data-testid="quick-party-email-input" />
              {partyKind === "customer" && <p className="text-xs text-muted-foreground">Sales invoices and estimates can be sent to this address.</p>}
            </div>
            <div className="space-y-2">
              <Label>Address line</Label>
              <Input value={quickParty.form.address} onChange={(e) => setQuickParty((q) => ({ ...q, form: { ...q.form, address: e.target.value } }))} data-testid="quick-party-address-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label>District</Label>
              <Input value={quickParty.form.district} onChange={(e) => setQuickParty((q) => ({ ...q, form: { ...q.form, district: e.target.value } }))} data-testid="quick-party-district-input" />
            </div>
            <div className="space-y-2">
              <Label>PIN code</Label>
              <Input
                inputMode="numeric"
                maxLength={6}
                value={quickParty.form.pin_code}
                onChange={(e) => setQuickParty((q) => ({ ...q, form: { ...q.form, pin_code: e.target.value } }))}
                placeholder="6-digit PIN code"
                data-testid="quick-party-pin-code-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>State</Label>
            <Select value={quickParty.form.state} onValueChange={(v) => setQuickParty((q) => ({ ...q, form: { ...q.form, state: v } }))}>
              <SelectTrigger data-testid="quick-party-state-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setQuickParty((q) => ({ ...q, open: false }))} data-testid="quick-party-cancel-button">Cancel</Button>
            <Button onClick={saveQuickParty} disabled={quickParty.saving} data-testid="quick-party-save-button">{quickParty.saving ? "Saving…" : "Add & Select"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <Dialog open={quickItem.open} onOpenChange={(v) => setQuickItem((q) => ({ ...q, open: v }))}>
      <DialogContent className="max-w-md" data-testid="quick-item-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">New Item</DialogTitle>
          <DialogDescription className="sr-only">Quickly add an inventory item without leaving this form</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input value={quickItem.form.name} onChange={(e) => setQuickItem((q) => ({ ...q, form: { ...q.form, name: e.target.value } }))} data-testid="quick-item-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label>HSN</Label>
              <Input value={quickItem.form.hsn} onChange={(e) => setQuickItem((q) => ({ ...q, form: { ...q.form, hsn: e.target.value } }))} data-testid="quick-item-hsn-input" />
            </div>
            <div className="space-y-2">
              <Label>GST %</Label>
              <Select value={String(quickItem.form.gst_rate)} onValueChange={(v) => setQuickItem((q) => ({ ...q, form: { ...q.form, gst_rate: Number(v) } }))}>
                <SelectTrigger data-testid="quick-item-gst-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GST_RATES.map((g) => <SelectItem key={g} value={String(g)}>{g}%</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Sale Price (₹)</Label>
              <Input type="number" min="0" step="any" value={quickItem.form.sale_price} onChange={(e) => setQuickItem((q) => ({ ...q, form: { ...q.form, sale_price: e.target.value } }))} data-testid="quick-item-sale-price-input" />
            </div>
            <div className="space-y-2">
              <Label>Purchase Price (₹)</Label>
              <Input type="number" min="0" step="any" value={quickItem.form.purchase_price} onChange={(e) => setQuickItem((q) => ({ ...q, form: { ...q.form, purchase_price: e.target.value } }))} data-testid="quick-item-purchase-price-input" />
            </div>
          </div>
          {pharma && (
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-2">
                <Label>Batch No.</Label>
                <Input value={quickItem.form.batch_no} onChange={(e) => setQuickItem((q) => ({ ...q, form: { ...q.form, batch_no: e.target.value } }))} data-testid="quick-item-batch-input" />
              </div>
              <div className="space-y-2">
                <Label>Mfg Date</Label>
                <Input type="date" value={quickItem.form.mfg_date} onChange={(e) => setQuickItem((q) => ({ ...q, form: { ...q.form, mfg_date: e.target.value } }))} data-testid="quick-item-mfg-input" />
              </div>
              <div className="space-y-2">
                <Label>Expiry Date</Label>
                <Input type="date" value={quickItem.form.exp_date} onChange={(e) => setQuickItem((q) => ({ ...q, form: { ...q.form, exp_date: e.target.value } }))} data-testid="quick-item-exp-input" />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setQuickItem((q) => ({ ...q, open: false }))} data-testid="quick-item-cancel-button">Cancel</Button>
            <Button onClick={saveQuickItem} disabled={quickItem.saving} data-testid="quick-item-save-button">{quickItem.saving ? "Saving…" : "Add & Use"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
